import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import { getJson, postJson, setCsrfToken } from '@/api/client'
import type { AuthUser, MenuNode, NextModule, TenantInfo } from '@/types/api'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const loading = ref(false)
  const error = ref('')

  const isAuthenticated = computed(() => !!user.value?.userid)
  const tenantId = computed(() => user.value?.tenant_id ?? 1)
  const isAdmin = computed(() => !!user.value?.is_admin || hasPermission('*'))
  const isPlatform = computed(
    () => !!user.value?.is_platform || !!user.value?.is_admin || hasPermission('platform.access'),
  )
  const impersonating = computed(() => !!user.value?.impersonating)
  const tenants = computed<TenantInfo[]>(() => user.value?.tenants ?? [])
  const menu = computed<MenuNode[]>(() => user.value?.menu ?? [])
  const modules = computed<NextModule[]>(() => user.value?.modules ?? [])

  function applyUser(u: AuthUser | null) {
    user.value = u
    if (u?.csrf_token) setCsrfToken(u.csrf_token)
  }

  function hasPermission(key: string) {
    if (!user.value) return false
    const perms = user.value.permissions || []
    if (perms.includes('*')) return true
    if (user.value.is_admin) return true
    return perms.includes(key)
  }

  /**
   * Classic menu event for the current group (ROLE.ADD_DATA, EDIT_DATA, …).
   * Prefer route when checking Next modules, e.g. hasEvent('EDIT_DATA', { route: '/jacket-store' }).
   */
  function hasEvent(
    eventName: string,
    opts?: { menuId?: number; route?: string },
  ): boolean {
    if (!user.value) return false
    if (user.value.is_admin || hasPermission('*')) return true
    const ev = (eventName || '').toUpperCase().trim()
    if (!ev) return false
    const cat = user.value.menu_events
    if (!cat) return false
    if (cat.is_admin_all) return true

    const menuId = opts?.menuId
    if (menuId != null && menuId > 0) {
      const byId = cat.by_menu_id as Record<string, Record<string, boolean>>
      const m = byId[String(menuId)] || byId[menuId as unknown as string]
      if (m && m[ev]) return true
    }
    let route = opts?.route
    if (route) {
      if (!route.startsWith('/')) route = '/' + route
      const variants = [route, route.replace(/\/$/, '') || '/']
      for (const r of variants) {
        if (cat.by_route[r] && cat.by_route[r][ev]) return true
      }
    }
    if (menuId == null && !opts?.route) {
      return (cat.flat || []).includes(ev)
    }
    return false
  }

  async function fetchMe() {
    loading.value = true
    error.value = ''
    try {
      const res = await getJson<AuthUser>('/me')
      if (res.success && res.data) {
        applyUser(res.data)
        if (res.csrf_token) setCsrfToken(res.csrf_token as string)
        return true
      }
      applyUser(null)
      return false
    } catch {
      applyUser(null)
      return false
    } finally {
      loading.value = false
    }
  }

  type LoginResult =
    | true
    | false
    | {
        stage: 'mfa'
        mfa_required?: boolean
        mfa_setup_required?: boolean
        username?: string
        tenant_id?: number
      }

  async function login(
    username: string,
    password: string,
    tenantCode?: string,
  ): Promise<LoginResult> {
    loading.value = true
    error.value = ''
    try {
      // Prime CSRF for session before login (optional GET)
      try {
        await getJson('/')
      } catch {
        /* ignore */
      }
      const body: Record<string, unknown> = { username, password }
      if (tenantCode) body.tenant_code = tenantCode
      const res = await postJson<AuthUser & { stage?: string; mfa_setup_required?: boolean; mfa_required?: boolean; username?: string; tenant_id?: number }>(
        '/auth/login',
        body,
      )
      if (!res.success) {
        error.value = res.message || 'Login failed'
        applyUser(null)
        return false
      }
      if ((res as { csrf_token?: string }).csrf_token) {
        setCsrfToken((res as { csrf_token?: string }).csrf_token as string)
      }
      const data = res.data
      if (data && (data as { stage?: string }).stage === 'mfa') {
        applyUser(null)
        return {
          stage: 'mfa',
          mfa_required: true,
          mfa_setup_required: !!(data as { mfa_setup_required?: boolean }).mfa_setup_required,
          username: (data as { username?: string }).username || username,
          tenant_id: (data as { tenant_id?: number }).tenant_id,
        }
      }
      applyUser(data as AuthUser)
      return true
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object') {
        const body = e.response.data as { message?: string }
        error.value = body.message || 'Login failed'
      } else {
        error.value = e instanceof Error ? e.message : 'Login failed'
      }
      applyUser(null)
      return false
    } finally {
      loading.value = false
    }
  }

  async function verifyMfa(code: string) {
    loading.value = true
    error.value = ''
    try {
      const res = await postJson<AuthUser>('/auth/mfa/verify', { code })
      if (!res.success || !res.data) {
        error.value = res.message || 'Invalid code'
        return false
      }
      applyUser(res.data)
      if ((res as { csrf_token?: string }).csrf_token) {
        setCsrfToken((res as { csrf_token?: string }).csrf_token as string)
      }
      return true
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object') {
        error.value = (e.response.data as { message?: string }).message || 'Invalid code'
      } else {
        error.value = e instanceof Error ? e.message : 'Invalid code'
      }
      return false
    } finally {
      loading.value = false
    }
  }

  async function mfaSetupBegin() {
    error.value = ''
    try {
      const res = await postJson<{
        secret: string
        otpauth_url: string
        issuer?: string
        account?: string
      }>('/auth/mfa/setup/begin', {})
      if (!res.success || !res.data) {
        error.value = res.message || 'Could not start MFA setup'
        return null
      }
      return res.data
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Could not start MFA setup'
      return null
    }
  }

  async function mfaSetupConfirm(code: string) {
    loading.value = true
    error.value = ''
    try {
      const res = await postJson<AuthUser>('/auth/mfa/setup/confirm', { code })
      if (!res.success || !res.data) {
        error.value = res.message || 'Invalid code'
        return false
      }
      // After enroll during login, data is full me payload
      if ((res.data as AuthUser).userid) {
        applyUser(res.data)
      }
      return true
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object') {
        error.value = (e.response.data as { message?: string }).message || 'Invalid code'
      } else {
        error.value = e instanceof Error ? e.message : 'Invalid code'
      }
      return false
    } finally {
      loading.value = false
    }
  }

  /** Clear local session state without calling the API (e.g. after 401 / expired cookie). */
  function clearSessionLocal() {
    applyUser(null)
    setCsrfToken('')
    error.value = ''
  }

  async function logout() {
    try {
      await postJson('/auth/logout')
    } finally {
      clearSessionLocal()
    }
  }

  async function switchTenant(tenantId: number) {
    try {
      const res = await postJson<AuthUser>('/tenants/switch', { tenant_id: tenantId })
      if (res.success && res.data) {
        applyUser(res.data)
        // Guard against stale session re-resolve (must match requested tenant)
        if (res.data.tenant_id != null && Number(res.data.tenant_id) !== Number(tenantId)) {
          error.value = 'Tenant switch did not stick; session still on another tenant'
          return false
        }
        return true
      }
      error.value = res.message || 'Tenant switch failed'
      return false
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object') {
        const body = e.response.data as { message?: string }
        error.value = body.message || 'Tenant switch failed'
      } else {
        error.value = e instanceof Error ? e.message : 'Tenant switch failed'
      }
      return false
    }
  }

  async function impersonate(userId: number) {
    const res = await postJson<AuthUser>('/auth/impersonate', { user_id: userId })
    if (res.success && res.data) {
      applyUser(res.data)
      return true
    }
    return false
  }

  async function stopImpersonate() {
    const res = await postJson<AuthUser>('/auth/stop-impersonate', {})
    if (res.success && res.data) {
      applyUser(res.data)
      return true
    }
    return false
  }

  function tenantLabel() {
    const t = user.value?.tenant as TenantInfo | undefined
    return t?.name || `Tenant #${tenantId.value}`
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    tenantId,
    isAdmin,
    isPlatform,
    impersonating,
    tenants,
    menu,
    modules,
    hasPermission,
    hasEvent,
    fetchMe,
    login,
    verifyMfa,
    mfaSetupBegin,
    mfaSetupConfirm,
    clearSessionLocal,
    logout,
    switchTenant,
    impersonate,
    stopImpersonate,
    tenantLabel,
  }
})
